import { createElement } from "react";
import { defaultTheme } from "react-admin";
import { HiOutlineChevronDown } from "react-icons/hi";
import { colors, fontFamily, layout, radius, shadows } from "./tokens";

// Tema ativo do painel (importado no App.js).
// Cores e tipografia vêm de tokens.ts — não usar hexadecimais aqui.
export const themeClean = {
  ...defaultTheme,
  palette: {
    mode: "light",
    primary: {
      main: colors.primary,
      dark: colors.primaryDark,
      contrastText: colors.textOnPrimary,
    },
    secondary: {
      main: colors.action,
      contrastText: colors.white,
    },
    success: {
      main: colors.success,
      contrastText: colors.white,
    },
    warning: {
      main: colors.warning,
      contrastText: colors.white,
    },
    error: {
      main: colors.action,
      contrastText: colors.white,
    },
    background: {
      default: colors.bgContent,
    },
    // O padrão do MUI para texto secundário é rgba(0,0,0,0.6) — cerca de
    // 4,6:1 sobre branco, no limite da norma. É a cor dos rótulos de campo
    // ("Data de Nascimento" e afins), que o react-admin renderiza com
    // color="textSecondary". Apontando para o token, sobe para 7,56:1.
    text: {
      primary: colors.textStrong,
      secondary: colors.textSecondary,
    },
  },
  typography: {
    fontFamily,
  },
  shape: {
    // Raio global do MUI (botões, campos, selects...) — escala em tokens.ts
    borderRadius: radius.sm,
  },
  sidebar: {
    width: layout.sidebarWidth,
    closedWidth: layout.sidebarClosedWidth,
  },
  components: {
    ...defaultTheme.components,
    // Página de erro que o react-admin renderiza quando a CONSULTA de
    // uma lista falha (API fora do ar). Esse caminho é hardcoded no
    // ListView (não aceita componente próprio, ao contrário do erro de
    // renderização, coberto pela AppErrorPage via <Layout error>). Os
    // textos já vêm das nossas traduções (i18n.ts); aqui sai o ar
    // "apagado" (opacity 0.5) e entram as cores da marca. O bloco de
    // links de desenvolvedor (advice) só existe em modo dev — ocultado
    // para o localhost não confundir; produção nunca o renderiza.
    RaError: {
      styleOverrides: {
        root: {
          opacity: 1,
          fontFamily: "inherit",
          padding: "40px 16px",
          "& .RaError-title": {
            color: colors.textStrong,
            fontSize: 22,
            fontWeight: 700,
          },
          "& .RaError-icon": {
            color: colors.action,
            width: "1.6em",
            height: "1.6em",
          },
          "& .RaError-advice": { display: "none" },
          // Dentro do quadro técnico (só dev), o 1º parágrafo repete a
          // mensagem que já está no resumo clicável — fica só o rastro
          // da quebra, quando existir
          "& .RaError-panelDetails p:first-of-type": { display: "none" },
        },
      },
    },
    // Botões sem sombra (box-shadow) em todo o painel
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
    },
    MuiButtonGroup: {
      defaultProps: {
        disableElevation: true,
      },
    },
    // Inputs no padrão da marca em todo o painel: a variante "filled"
    // do react-admin é MANTIDA (todo o layout de formulários/filtros é
    // calibrado para ela), mas re-estilizada — fundo branco, borda dos
    // tokens, sem sublinhado, hover intermediário e foco azul da marca
    MuiFilledInput: {
      defaultProps: {
        disableUnderline: true,
      },
      styleOverrides: {
        root: {
          backgroundColor: colors.white,
          border: `2px solid ${colors.border}`,
          borderRadius: radius.sm,
          // Foco suave: borda e anel entram em transição, sem corte seco
          transition:
            "border-color 0.25s ease, box-shadow 0.25s ease, background-color 0.25s ease",
          "&:hover": {
            backgroundColor: colors.white,
            borderColor: colors.primaryHover,
          },
          "&.Mui-focused": {
            backgroundColor: colors.white,
            borderColor: colors.primary,
            // Anel de 12% do azul da marca (token + alpha em hex)
            boxShadow: `0 0 0 3px ${colors.primary}1F`,
            outline: "none",
          },
          // Remove o anel de foco nativo do navegador (ficava um segundo
          // contorno azul por fora); o foco é sinalizado pela borda + anel
          "& input:focus, & textarea:focus, & .MuiInputBase-input:focus": {
            outline: "none",
          },
          "&.Mui-disabled": {
            backgroundColor: colors.neutral50,
          },
        },
      },
    },
    // Seta dos dropdowns: chevron fino da família Heroicons no lugar
    // do triângulo padrão do MUI — em todos os selects e autocompletes
    MuiSelect: {
      defaultProps: {
        IconComponent: HiOutlineChevronDown,
      },
      styleOverrides: {
        icon: {
          fontSize: 20,
          top: "calc(50% - 10px)",
          color: colors.primaryDark,
          // gira suave ao abrir/fechar (o MUI aplica rotate(180deg))
          transition: "transform 0.25s ease",
        },
      },
    },
    MuiAutocomplete: {
      defaultProps: {
        popupIcon: createElement(HiOutlineChevronDown, { size: 20 }),
      },
      styleOverrides: {
        popupIndicator: {
          color: colors.primaryDark,
          transition: "transform 0.25s ease",
        },
      },
    },
    // Rótulos dos campos com mais contraste: peso 600 em repouso,
    // navy escuro quando flutuam sobre o campo e azul primário no foco
    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          color: colors.textMuted,
          "&.Mui-focused": { color: colors.primary },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        shrink: {
          color: colors.primaryDark,
          "&.Mui-focused": { color: colors.primary },
        },
      },
    },
    // Títulos de seção (CardHeader) alinhados com os campos: o padding
    // lateral próprio somava com o do Card e empurrava o título p/ dentro
    MuiCardHeader: {
      styleOverrides: {
        root: {
          // Título encostado nos campos: só respiro embaixo
          padding: "0px 0px 10px 0px",
        },
        // O h5 padrão do MUI (24px, peso 400) competia com o conteúdo do
        // card: menor e mais pesado marca a seção sem gritar
        title: {
          fontSize: 18,
          fontWeight: 700,
          color: colors.textStrong,
        },
      },
    },
    // Rótulos dos campos nas telas de visualização ("Data de Nascimento",
    // "Atividade Profissional Principal"...). O react-admin fixa
    // `fontSize: 0.75em` neles — e por ser relativo, encolhe mais ainda
    // dentro de blocos com fonte reduzida. Fixado em px, com mais peso e
    // no tom de texto legível dos tokens.
    RaLabeled: {
      styleOverrides: {
        root: {
          // Rótulo: subordinado. Claro e leve, mas em 12px fixos —
          // o 0.75em do react-admin caía em ~11px e variava com o contexto.
          "& .RaLabeled-label": {
            fontSize: 12,
            fontWeight: 500,
            color: colors.textMuted,
            marginBottom: 1,
          },
          // Valor: é o que se lê. A hierarquia vem daqui — maior, mais
          // pesado e mais escuro — e não de clarear o rótulo, que
          // precisa continuar legível.
          "& > *:not(.RaLabeled-label)": {
            fontSize: 14.5,
            fontWeight: 600,
            color: colors.textStrong,
          },
        },
      },
    },
    // Barra de "N selecionados" (ações em massa das tabelas): o react-admin
    // embrulha os botões num TopToolbar que herda alignItems flex-end e
    // paddings verticais do RaTopToolbar dentro de uma barra de 48px — o
    // botão da direita (Remover, Operações em massa…) ficava ancorado
    // embaixo, desalinhado do rótulo, e transbordava quando mais alto.
    // Centralizado no eixo vertical, zerando os paddings herdados.
    RaBulkActionsToolbar: {
      styleOverrides: {
        root: {
          "& .RaBulkActionsToolbar-topToolbar": {
            alignItems: "center",
            paddingTop: 0,
            paddingBottom: 0,
          },
          "& .RaBulkActionsToolbar-title": {
            alignItems: "center",
          },
        },
      },
    },
    // Diálogo de confirmação (exclusões via AppDeleteButton): cartão
    // mais estreito que o padrão do MUI, CONFIRMAR em vermelho (é a
    // ação destrutiva) e Cancelar neutro. O botão Cancelar não tem
    // classe própria no react-admin — é o primeiro do DialogActions.
    RaConfirm: {
      styleOverrides: {
        root: {
          "& .MuiDialog-paper": {
            maxWidth: 420,
            width: "100%",
            borderRadius: radius.lg,
          },
          // Respiro dos botões: o padding padrão do DialogActions (8px)
          // deixava Cancelar/Confirmar colados na borda inferior/lateral
          "& .MuiDialogActions-root": {
            padding: "8px 20px 20px",
          },
          "& .MuiDialogActions-root .MuiButton-root:first-of-type": {
            color: colors.textMuted,
          },
          // Confirmar como botão sólido vermelho (ação destrutiva em
          // destaque); Cancelar continua texto neutro
          "& .RaConfirm-confirmPrimary": {
            backgroundColor: colors.action,
            color: colors.white,
            paddingLeft: 16,
            paddingRight: 16,
            "&:hover": {
              backgroundColor: colors.action,
              filter: "brightness(0.92)",
            },
          },
        },
      },
    },
    // Editor de texto rico (ra-input-rich-text/TipTap, usado no corpo
    // dos Templates de E-mail): o padrão do pacote usa o fundo lavanda
    // da página, borda fina escura e altura de uma linha — aqui entra
    // no desenho dos campos da marca (fundo branco, borda de token,
    // foco com anel azul) e com altura de área de texto
    RaRichTextInput: {
      styleOverrides: {
        root: {
          "& .RaRichTextInput-editorContent .ProseMirror": {
            backgroundColor: colors.white,
            border: `2px solid ${colors.border}`,
            borderRadius: radius.sm,
            minHeight: 180,
            padding: "12px 16px",
            fontSize: 14,
            transition:
              "border-color 0.25s ease, box-shadow 0.25s ease, background-color 0.25s ease",
            "&:hover": {
              backgroundColor: colors.white,
              borderColor: colors.primaryHover,
            },
            "&:focus, &.ProseMirror-focused": {
              backgroundColor: colors.white,
              borderColor: colors.primary,
              boxShadow: `0 0 0 3px ${colors.primary}1F`,
              outline: "none",
            },
          },
        },
      },
    },
    // Badges/chips padronizados no painel inteiro: mesma altura,
    // raio da escala global e peso de fonte — qualquer <Chip> herda
    MuiChip: {
      styleOverrides: {
        root: {
          height: 22,
          borderRadius: radius.sm,
          fontSize: 12,
          fontWeight: 600,
        },
        label: {
          paddingLeft: 8,
          paddingRight: 8,
        },
      },
    },
    // Tooltips no padrão da marca em todo o painel: navy do logotipo,
    // seta apontando para o alvo, cantos da escala global e entrada
    // suave (transição de 250ms, como o foco dos campos)
    MuiTooltip: {
      defaultProps: {
        arrow: true,
        enterDelay: 200,
        TransitionProps: { timeout: 250 },
      },
      styleOverrides: {
        tooltip: {
          backgroundColor: colors.primaryDark,
          color: colors.white,
          fontSize: 12.5,
          fontWeight: 500,
          lineHeight: 1.45,
          padding: "7px 12px",
          borderRadius: radius.sm,
          boxShadow: shadows.popover,
          maxWidth: 360,
        },
        arrow: {
          color: colors.primaryDark,
        },
      },
    },
    // Rodapé de paginação das listas: "Linhas por página" à esquerda,
    // contagem e navegação juntas à direita.
    //
    // No MUI a ordem é: espaçador, rótulo, seletor, contagem, botões — com
    // o espaçador flexível ANTES de tudo, empurrando o conjunto inteiro
    // para a direita. Em vez de empurrar cada peça com margem automática
    // (o que reparte o espaço livre e deixa a contagem no meio), o rótulo
    // e o seletor são reordenados para antes do espaçador. Ele então
    // absorve a folga no meio, e contagem e botões ficam juntos na ponta.
    MuiTablePagination: {
      styleOverrides: {
        selectLabel: { order: -2 },
        input: { order: -1 },
        spacer: { flex: "1 1 auto" },
        // "Linhas por página" encostado na borda esquerda do cartão.
        // O gutter do Toolbar tem media query própria (24px acima de
        // 600px) — repete o breakpoint para garantir a precedência.
        toolbar: {
          paddingLeft: 0,
          "@media (min-width: 600px)": { paddingLeft: 0 },
        },
      },
    },
    // Os avisos (toasts) do painel são renderizados por
    // components/AppNotification.tsx, passado ao <Admin> — não pelo
    // Notification padrão do react-admin. O estilo deles fica lá.
  },
};
